import { z } from "zod";
/** UUID v4 identifier, per `02_SYSTEM_ARCHITECTURE.md` §6.3. */
export const uuidSchema = z.uuid();
/**
 * Fixed-precision money amount, serialized as a decimal string to avoid floating-point drift
 * across the JSON boundary. `00_CLAUDE_CODE_MASTER_PROMPT.md` §9: "Money must use fixed-precision
 * decimal database types; never floating point."
 */
export const moneyAmountSchema = z.string().regex(/^-?\d+(\.\d{1,2})?$/, "Amount must be a decimal string with at most 2 fraction digits");
/** ISO-4217 currency code: exactly three letters (any case in, upper case out) — never blank (KG-121). */
export const currencyCodeSchema = z
    .string()
    .trim()
    .regex(/^[A-Za-z]{3}$/, "Use a 3-letter ISO-4217 currency code, e.g. BDT")
    .transform((value) => value.toUpperCase());
/** IANA timezone identifier string (format-checked, not validated against the tz database). */
export const timezoneSchema = z
    .string()
    .min(1)
    .regex(/^[A-Za-z0-9_+\-/]+$/);
/**
 * An API base URL that's either a full absolute URL (`http://localhost:3000/api/v1` — typical
 * for local dev, where the browser and API are on different origins) or a root-relative path
 * (`/api/v1` — typical behind a reverse proxy that puts the API on the same origin as the app,
 * e.g. `infra/nginx/proxy.conf`). A bare `z.url()` rejects the second, entirely legitimate case.
 */
export const apiBaseUrlSchema = z
    .string()
    .min(1)
    .refine((value) => value.startsWith("/") || z.url().safeParse(value).success, 'Must be an absolute URL or a root-relative path (starting with "/")');
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(20),
});
/**
 * A boolean query-string flag. `z.coerce.boolean()` is wrong for query strings — it uses JS
 * truthiness, so `"false"` becomes `true`. This accepts only `true`/`false`/`1`/`0` (any case).
 */
export const queryBooleanSchema = z
    .union([z.boolean(), z.enum(["true", "false", "TRUE", "FALSE", "True", "False", "1", "0"])])
    .transform((value) => value === true || value === "1" || (typeof value === "string" && value.toLowerCase() === "true"));
/**
 * The PATCH counterpart of a create schema: every field optional and **no field defaulted**.
 *
 * `create.partial()` is wrong for updates under Zod 4 — a `.default()` still fires inside `.partial()`, so
 * a PATCH that leaves `status` out parses as `status: "active"` and silently resets it (UAT Phase 22 found
 * edits re-activating inactive campuses and re-mandating elective subjects). This unwraps each top-level
 * default (also under `.optional()` / `.nullable()`) before making the fields optional, so an update
 * changes only what it sends. Chain `.omit()` / `.extend()` on the result as with `.partial()`.
 */
export function partialWithoutDefaults(schema) {
    const stripDefault = (field) => {
        if (field instanceof z.ZodDefault)
            return stripDefault(field.unwrap());
        if (field instanceof z.ZodOptional)
            return stripDefault(field.unwrap()).optional();
        if (field instanceof z.ZodNullable)
            return stripDefault(field.unwrap()).nullable();
        return field;
    };
    const shape = Object.fromEntries(Object.entries(schema.shape).map(([key, field]) => [key, stripDefault(field)]));
    return schema.extend(shape).partial();
}
//# sourceMappingURL=primitives.js.map